import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";

export function AboutPage() {
  return (
    <div>
      <SEO
        title="About Us"
        description="GetSent helps businesses protect their email reputation with DMARC monitoring and email authentication tools. Learn about our mission and approach."
        canonical="/about"
      />
      {/* Header */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-slate-50/50 to-transparent py-16 dark:from-slate-950/20 sm:py-20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">About GetSent</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Built by a small team that wanted a simpler way to make sure email actually gets delivered. Designed for small businesses, at a price that makes sense.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="prose prose-slate dark:prose-invert max-w-none">
            {/* Logo */}
            <div className="flex justify-center mb-12">
              <img
                src="/logo.png"
                alt="GetSent Logo"
                className="h-16 w-auto"
              />
            </div>

            <h2 className="mt-10 text-xl font-semibold">Our Mission</h2>
            <p className="mt-4 text-muted-foreground">
              Email authentication shouldn't be complicated or expensive. We built GetSent because
              we were frustrated with overly complex enterprise tools that charge too much for what
              small businesses actually need. Our mission is to help you protect your domain
              from email spoofing, improve your email deliverability, and gain visibility into who's
              sending email on your behalf, without breaking the bank.
            </p>

            <h2 className="mt-10 text-xl font-semibold">What We Do</h2>
            <p className="mt-4 text-muted-foreground">
              GetSent provides comprehensive DMARC monitoring and email authentication tools:
            </p>
            <ul className="mt-4 list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>DMARC Report Processing:</strong> We automatically receive, parse, and analyze your DMARC aggregate reports, turning complex XML data into actionable insights.</li>
              <li><strong>Real-time Dashboards:</strong> Monitor your email authentication status with intuitive visualizations showing pass rates, sending sources, and trends over time.</li>
              <li><strong>DNS Tools:</strong> Validate and generate SPF, DKIM, and DMARC records with our free tools to ensure your email authentication is properly configured.</li>
              <li><strong>Alerts & Notifications:</strong> Stay informed about authentication failures and unauthorized senders attempting to use your domain.</li>
            </ul>

            <h2 className="mt-10 text-xl font-semibold">Why DMARC Matters</h2>
            <p className="mt-4 text-muted-foreground">
              Email spoofing costs businesses billions of dollars annually through phishing attacks
              and brand impersonation. DMARC (Domain-based Message Authentication, Reporting & Conformance)
              is the industry standard for preventing unauthorized use of your domain in email.
            </p>
            <p className="mt-4 text-muted-foreground">
              Without DMARC monitoring, you're flying blind. You won't know if attackers are spoofing
              your domain, if legitimate emails are failing authentication, or if your email
              deliverability is being impacted. GetSent gives you the visibility you need.
            </p>

            <h2 className="mt-10 text-xl font-semibold">Our Approach</h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <div className="rounded-xl border bg-muted/30 p-6">
                <h3 className="font-semibold">Simple Setup</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Add your domain, update your DMARC record, and start receiving insights within 24-48 hours. No complex configuration required.
                </p>
              </div>
              <div className="rounded-xl border bg-muted/30 p-6">
                <h3 className="font-semibold">Privacy First</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  We only process the data necessary to provide our service. Your reports are encrypted and never shared with third parties.
                </p>
              </div>
              <div className="rounded-xl border bg-muted/30 p-6">
                <h3 className="font-semibold">Fair Pricing</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Start free and scale as you grow. Our pricing is based on actual usage, not artificial feature gates.
                </p>
              </div>
              <div className="rounded-xl border bg-muted/30 p-6">
                <h3 className="font-semibold">Developer Friendly</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Built by developers, for developers. Clean interfaces, clear documentation, and tools that just work.
                </p>
              </div>
            </div>

            <h2 className="mt-10 text-xl font-semibold">Get Started</h2>
            <p className="mt-4 text-muted-foreground">
              Ready to protect your email reputation? Sign up for free and start monitoring your
              DMARC reports today.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link
                to="/register"
                className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Start for Free
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center rounded-lg border bg-white px-5 py-2.5 text-sm font-medium text-slate-900 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700"
              >
                View Pricing
              </Link>
            </div>

            <h2 className="mt-10 text-xl font-semibold">Contact Us</h2>
            <p className="mt-4 text-muted-foreground">
              Have questions or need help? We'd love to hear from you.
            </p>
            <ul className="mt-4 list-none pl-0 text-muted-foreground space-y-2">
              <li>
                <strong>General inquiries:</strong>{" "}
                <a href="mailto:hello@getsent.dev" className="text-indigo-600 hover:text-indigo-500">
                  hello@getsent.dev
                </a>
              </li>
              <li>
                <strong>Sales:</strong>{" "}
                <a href="mailto:sales@getsent.dev" className="text-indigo-600 hover:text-indigo-500">
                  sales@getsent.dev
                </a>
              </li>
              <li>
                <strong>Support:</strong>{" "}
                <a href="mailto:support@getsent.dev" className="text-indigo-600 hover:text-indigo-500">
                  support@getsent.dev
                </a>
              </li>
            </ul>
          </div>

          <div className="mt-12 flex items-center gap-4 text-sm text-muted-foreground">
            <Link to="/pricing" className="hover:text-foreground">
              Pricing
            </Link>
            <span>&middot;</span>
            <Link to="/privacy" className="hover:text-foreground">
              Privacy Policy
            </Link>
            <span>&middot;</span>
            <Link to="/terms" className="hover:text-foreground">
              Terms of Service
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
